import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8911 = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8911: Alternative Fuel Credits for Form 990-T | TaxZerone"
          description="Learn how to file IRS Form 8911 with Form 990-T to claim alternative fuel credits. Discover eligibility, filing instructions, and ways to reduce tax liability."
          keywords=""
          canonical="resources/tax-exempt/form-8911-alternative-fuel-credits/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form{' '}
          <span className="highlight-container">
            <span className="highlight"> 8911</span>
          </span>{' '}
          Alternative Fuel Vehicle Refueling
          <br /> Property Credit
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-8911-alternative-fuel-credits/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8911 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Form 8911? Form 8911, Alternative Fuel Vehicle
                          Refueling Property Credit,
                        </strong>{' '}
                        is used to calculate the credit for qualifying{' '}
                        <strong style={{ fontWeight: 600 }}>
                          electric vehicle charging and alternative fuel
                          refueling property{' '}
                        </strong>
                        placed in service during the tax year.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Can Use It? Businesses, tax-exempt organizations,
                          individuals, partnerships, and S corporations
                        </strong>{' '}
                        may use Form 8911 when they meet the applicable
                        eligibility requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Form 8911 calculates the credit for qualifying{' '}
                        <strong style={{ fontWeight: 600 }}>
                          refueling or electric vehicle charging property,
                        </strong>{' '}
                        including the applicable business/ investment or
                        personal-use portion.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Can Tax-Exempt Organizations Claim the Form 8911
                          Credit?
                        </strong>{' '}
                        Yes. Eligible tax-exempt organizations may claim the
                        credit through an{' '}
                        <strong style={{ fontWeight: 600 }}>
                          elective payment election.
                        </strong>{' '}
                        They generally use{' '}
                        <strong style={{ fontWeight: 600 }}>Form 8911,</strong>
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-8911-alternative-fuel-credits/#scha"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Schedule A (Form 8911)
                        </Link>
                        ,{' '}
                        <Link
                          href="https://www.taxzerone.com/resources/tax-exempt/form-3800-business-credits/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          Form 3800
                        </Link>{' '}
                        <strong style={{ fontWeight: 600 }}>and</strong>{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990-t/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990-T
                        </Link>{' '}
                        to report the applicable credit.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#new" sx={{ color: '#0202ff' }}>
                          What’s New?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 8911?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#=who" sx={{ color: '#0202ff' }}>
                          Who must file Form 8911?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#apay" sx={{ color: '#0202ff' }}>
                          Applicable Entities and Elective Payment Election
                          (EPE)
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#filing" sx={{ color: '#0202ff' }}>
                          Form 8911 Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#scha" sx={{ color: '#0202ff' }}>
                          Schedule A (Form 8911) - Alternative Fuel Vehicle
                          Refueling Property
                        </Link>
                        <List>
                          <ListItem sx={{ pt: 0.5, alignItems: 'flex-start' }}>
                            <ListItemIcon
                              sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                            >
                              <DoubleArrowIcon />
                            </ListItemIcon>
                            <ListItemText>
                              <Link href="#howto" sx={{ color: '#0202ff' }}>
                                How to Complete Schedule A (Form 8911):
                                Line-by-Line Instructions
                              </Link>
                            </ListItemText>
                          </ListItem>
                        </List>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="new"
                >
                  What's New?
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Termination Date Updated
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The One Big Beautiful Bill Act (P.L. 119-21) revised the
                  expiration date for the Alternative Fuel Vehicle Refueling
                  Property Credit under Section 30C. As a result, the credit is
                  now scheduled to terminate on{' '}
                  <span style={{ fontWeight: 600 }}> June 30, 2026,</span>{' '}
                  instead of{' '}
                  <span style={{ fontWeight: 600 }}> December 31, 2032</span>.
                  Taxpayers generally cannot claim the credit for qualified
                  alternative fuel vehicle refueling property placed in service
                  after June 30, 2026.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  New Reporting for Item A
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Beginning with tax years that start after 2024, taxpayers must
                  report the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total number of qualified alternative fuel vehicle refueling
                    properties on Item A of Form 8911
                  </span>
                  . This change provides the IRS with additional information
                  regarding the properties for which the credit is being
                  claimed.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                    mb: 1,
                  }}
                >
                  Form 7220 - Increased Credit Claims
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Taxpayers claiming the increased credit amount based on
                  compliance with the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Prevailing Wage and Apprenticeship (PWA)
                  </span>
                  requirements must file Form 7220, Prevailing Wage and
                  Apprenticeship (PWA) Verification and Corrections. A{' '}
                  <span style={{ fontWeight: 600 }}>separate Form 7220</span> is
                  required for each qualified property for which the increased
                  credit amount is claimed.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="whatis"
                >
                  What Is Form 8911?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 8911, Alternative Fuel Vehicle Refueling Property Credit,
                  is used to calculate and claim a tax credit for qualified
                  alternative fuel vehicle refueling property placed in service
                  during the tax year. The credit helps offset eligible
                  installation costs and encourages investment in alternative
                  fuel vehicle infrastructure.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Qualifying Property for Form 8911
                </Typography>
                <Typography variant="body1">
                  The credit may be available for qualified refueling property,
                  including:
                </Typography>
                <ul style={{ marginBottom: 16 }}>
                  <li style={{ marginBottom: 8 }}>
                    Electric vehicle (EV) charging equipment
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Hydrogen refueling equipment
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Natural gas refueling property
                  </li>
                  <li style={{ marginBottom: 8 }}>
                    Propane refueling property
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  To qualify, the property must be placed in service during the
                  tax year and meet applicable IRS requirements.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="who"
                >
                  Who must file Form 8911?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Generally, any organization that is subject to UBIT and has
                  invested in alternative fuel vehicle refueling property must
                  file Form 8911.
                </Typography>

                <ul style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Individuals and Businesses:
                    </strong>{' '}
                    Those who have invested in alternative fuel vehicle
                    refueling property at their home or business.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      {' '}
                      Partnerships and S corporations:
                    </strong>{' '}
                    They must file this form to claim the credit.
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> All other
                  taxpayers aren’t required to complete or file this form if
                  their only source for this credit is a partnership or S
                  corporation.
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Choose TaxZerone for a filing experience that goes
                        beyond expectations.
                      </Typography>
                      <Typography variant="body2">
                        Empower your tax compliance journey with us today!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form8911`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                {/* <Alert severity="warning" icon={false} sx={{ mb: 4 }} >
                  <strong style={{fontWeight:600}} >Note:</strong> The deadline is extended to the next business day
                  if the due date falls on a weekend or a legal holiday.
                </Alert> */}
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="apay"
                >
                  Applicable Entities and Elective Payment Election (EPE)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Certain organizations may qualify as{' '}
                  <span style={{ fontWeight: 600 }}> applicable entities</span>{' '}
                  under Section 6417 and elect to receive the Alternative Fuel
                  Vehicle Refueling Property Credit as a payment from the IRS
                  through an{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Elective Payment Election (EPE)
                  </span>
                  , also known as{' '}
                  <span style={{ fontWeight: 600 }}> Direct Pay</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0, fontWeight: 600 }}>
                  Eligible Applicable Entities
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Tax-exempt organizations</li>
                  <li>State and local governments</li>
                  <li>Indian tribal governments</li>
                  <li>Alaska Native Corporations</li>
                  <li>Rural electric cooperatives</li>
                  <li>
                    Certain U.S. territories and their political subdivisions
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Required Forms
                </Typography>
                <Typography variant="body1">
                  Organizations making an EPE generally must file:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Form 8911</li>
                  <li>
                    {' '}
                    <Link href="#scha" sx={{ color: '#0202ff' }}>
                      {' '}
                      Schedule A (Form 8911)
                    </Link>{' '}
                  </li>
                  <li>
                    {' '}
                    <Link
                      href="/resources/tax-exempt/form-3800-business-credits/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 3800{' '}
                    </Link>{' '}
                  </li>
                  <li>
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-t/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-T
                    </Link>{' '}
                    or another applicable tax return
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Pre-Filing Registration
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Before filing the tax return, organizations intending to make
                  an EPE or transfer election must complete the IRS pre-filing
                  registration process for each qualified property or project
                  associated with the credit. Upon successful registration, the
                  IRS issues a registration number that must be included on the
                  applicable tax forms when claiming the credit.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="filing"
                >
                  Form 8911: Line-by-Line Instruction
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8911-alternative-img.webp" />
                  <source srcSet="/assets/images/f8911-alternative-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8911-alternative-img.png"
                    loading="lazy"
                    alt="Form 8911, Alternative Fuel Vehicle Refueling Property Credit Organization details"
                    width={760}
                    height={151}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Before completing the credit calculation section, taxpayers
                  must provide basic identifying information.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Name(s) Shown on Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}> taxpayer's name</span>{' '}
                  exactly as it appears on the federal tax return to which Form
                  8911 is attached. The name entered on Form 8911 should match
                  the name reported on the corresponding tax return.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Identifying Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Identifying Number Enter the taxpayer's identifying number.
                </Typography>

                <ol style={{ marginBottom: 30 }}>
                  <li style={{ marginBottom: 16 }}>
                    Individuals should enter their Social Security Number (SSN).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Businesses and tax-exempt organizations should enter their
                    Employer Identification Number (EIN).
                  </li>
                </ol>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Item A – Total Number of Qualified Refueling Properties
                </Typography>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total number of qualified alternative fuel vehicle refueling
                    properties
                  </span>{' '}
                  for which you are claiming the credit during the tax year.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    A separate{' '}
                    <span style={{ fontWeight: 600 }}>
                      <Link href="#scha" sx={{ color: '#0202ff' }}>
                        {' '}
                        Schedule A (Form 8911)
                      </Link>
                    </span>{' '}
                    must be completed and attached for each qualified property
                    placed in service. The number entered on Item A should equal
                    the total number of Schedule A forms filed with Form 8911.
                    If the only credit being claimed is a pass-through credit
                    received from a partnership or S corporation and reported on
                    <span style={{ fontWeight: 600 }}> Line 2</span>, enter{' '}
                    <span style={{ fontWeight: 600 }}> 0</span> on Item A
                    because no qualified refueling property was directly placed
                    in service by the taxpayer.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    fontSize: 14,
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(102, 75, 0)',
                  }}
                >
                  <span style={{ fontWeight: 600 }}>For Example: </span> If an
                  organization placed two qualified EV charging stations in
                  service during the tax year, it should:
                  <ul>
                    <li style={{ marginBottom: 8 }}>Enter 2 in Item A.</li>
                    <li>Attach two separate Schedule A (Form 8911) forms.</li>
                  </ul>
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="filing"
                >
                  Part I - Credit for Business/Investment Use Part of Refueling
                  Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8911-part-1-credit-img.webp" />
                  <source srcSet="/assets/images/f8911-part-1-credit-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8911-part-1-credit-img.png"
                    loading="lazy"
                    alt="Form 8911, Part I - Credit for Business/Investment Use Part of Refueling Property Line 1 - Line 3"
                    width={760}
                    height={113}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1 - Credit From Schedule A (Form 8911)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total credit amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part II of all Schedule A (Form 8911)
                  </span>{' '}
                  forms attached to the return. This amount represents the total
                  credit calculated for qualified alternative fuel vehicle
                  refueling property placed in service during the tax year.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2 - Pass-Through Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter any Alternative Fuel Vehicle Refueling Property Credits
                  received from a partnership or S corporation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  These credits are reported on:
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Schedule K-1 (
                    <Link
                      href="/business-tax/efile-irs-form-1065-online/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1065
                    </Link>
                    ),
                  </li>
                  <li>
                    <Link
                      href="/resources/business-tax/schedule-k-1/"
                      sx={{ color: '#0202ff' }}
                    >
                      Schedule K-1
                    </Link>{' '}
                    (
                    <Link
                      href="/business-tax/efile-irs-form-1120-s/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1120-S
                    </Link>
                    ) ,
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Partnerships and S corporations must report passed-through
                  credits on this line. Other taxpayers claiming the credit on
                  Form 8911 should also include any eligible pass-through
                  credits received during the tax year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your only Alternative Fuel Vehicle Refueling Property
                  Credit is a pass-through credit received from a partnership or
                  S corporation, you may report the credit directly on{' '}
                  <Link
                    href="/resources/tax-exempt/form-3800-business-credits/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 3800
                  </Link>
                  , <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part III, Line 1s,
                  </span>{' '}
                  instead of filing Form 8911.
                </Typography>
                <Alert
                  severity="warning"
                  sx={{
                    fontSize: 14,
                    mt: 1,
                    mb: 3,
                    borderLeft: '3px solid rgb(102, 75, 0)',
                  }}
                >
                  <strong style={{ fontWeight: 600 }}> Caution:</strong> If you
                  receive a Schedule K-1 (
                  <Link
                    href="/resources/business-tax/form-1065-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1065
                  </Link>
                  ) or a Schedule K-1 (
                  <Link
                    href="/resources/business-tax/form-1120-s-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 1120-S
                  </Link>
                  ), refer to the{' '}
                  <Link
                    href="/resources/tax-exempt/form-3800-business-credits/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 3800 instructions
                  </Link>{' '}
                  for the reporting requirements applicable to transfer credits
                  under Section 6418.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 3 - Partnerships and S Corporations
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Partnerships and S corporations that elect to transfer all or
                  a portion of their Alternative Fuel Vehicle Refueling Property
                  Credit under Section 6418 must{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    report the total credit on Form 3800, Part III, Line 1s.
                  </span>
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  In these cases, the credit is reported on{' '}
                  <Link
                    href="/resources/tax-exempt/form-3800-business-credits/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 3800
                  </Link>{' '}
                  rather than being passed through to partners or shareholders
                  on{' '}
                  <Link
                    href="/resources/business-tax/schedule-k/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Schedule K
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 88,
                  }}
                  id="filing"
                >
                  Part II - Credit for Personal Use Part of Refueling Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8911-part-2-personal-img.webp" />
                  <source srcSet="/assets/images/f8911-part-2-personal-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8911-part-2-personal-img.png"
                    loading="lazy"
                    alt="Form 8911, Part II - Credit for Personal Use Part of Refueling Property Line 4 - Line 7"
                    width={760}
                    height={194}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 4 - Credit of Schedule A (Form 8911)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total credit amount from{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Part III of all Schedule A (Form 8911)
                  </span>{' '}
                  forms attached to the return. This amount represents the
                  credit calculated for qualified alternative fuel vehicle
                  refueling property reported in Part III of Schedule A.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 5 - Regular Tax Before Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter your regular tax liability before applying any credits.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Individuals </span>{' '}
                    should enter the total of Form 1040, Form 1040-SR, or Form
                    1040-NR, Line 16, plus Schedule 2 (Form 1040), Line 1z.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> All other filers</span>{' '}
                    must enter the regular tax before credits as reported on
                    their applicable tax return.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 6 - Tax-Reducing Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 6 is used to report certain credits that reduce your
                  regular tax liability before the Alternative Fuel Vehicle
                  Refueling Property Credit is applied.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 6a - Foreign Tax Credit
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    foreign tax credit claimed
                  </span>{' '}
                  for the tax year. This is the amount used to reduce your
                  regular tax liability before calculating the Alternative Fuel
                  Vehicle Refueling Property Credit.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 6b - Certain Allowable Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    total of other eligible credits
                  </span>{' '}
                  that reduce your regular tax before applying the Alternative
                  Fuel Vehicle Refueling Property Credit.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Individuals filing{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 1040, Form 1040-SR, or Form 1040-NR
                    </span>
                    should include the total of any credits or adjustments
                    reported on{' '}
                    <span style={{ fontWeight: 600 }}> Line 19</span> of their
                    return, along with the applicable credits reported on
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule 3 (Form 1040)
                    </span>
                    , Lines 2 through 5 and Line 7.
                  </li>
                  <li>
                    Estates and trusts filing Form 1041 should enter eligible
                    write-in credits reported on{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Schedule G, Line 2e
                    </span>
                    .
                  </li>
                  <li>
                    Do not include any amounts already reported as a general
                    business credit, prior-year minimum tax credit, or credit to
                    holders of tax credit bonds when calculating Line 6b.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 6c - Total Credits
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts on{' '}
                  <span style={{ fontWeight: 600 }}> Lines 6a and 6b</span> and
                  enter the result on Line 6c. This represents the total credits
                  that reduce regular tax before the Alternative Fuel Vehicle
                  Refueling Property Credit is applied.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 7 - Net Regular Tax
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract <span style={{ fontWeight: 600 }}> Line 6c</span>{' '}
                    from <span style={{ fontWeight: 600 }}> Line 5</span> and
                    enter the{' '}
                    <span style={{ fontWeight: 600 }}> result on Line 7</span>.
                    This amount represents your net regular tax after applying
                    the credits reported on Line 6.
                  </li>
                  <li>
                    If the result is zero or less, enter{' '}
                    <span style={{ fontWeight: 600 }}> 0 </span>on Line 7.
                    Generally, no further calculations are required, and Form
                    8911 does not need to be filed unless you are claiming a
                    credit reported on{' '}
                    <span style={{ fontWeight: 600 }}> Line 3</span>.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f8911-part-2-line8-img.webp" />
                  <source srcSet="/assets/images/f8911-part-2-line8-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8911-part-2-line8-img.png"
                    loading="lazy"
                    alt="Form 8911, Part II - Credit for Personal Use Part of Refueling Property Line 8 - Line 10"
                    width={760}
                    height={128}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 8 - Tentative Minimum Tax (TMT)
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter your{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Tentative Minimum Tax (TMT)
                    </span>{' '}
                    on Line 8. Even if you do not owe the Alternative Minimum
                    Tax (AMT), you must still calculate the TMT to determine the
                    allowable “Alternative Fuel Vehicle Refueling Property
                    Credit.”
                  </li>
                  <li>
                    Complete and attach the applicable AMT form or schedule and
                    enter the calculated TMT amount on this line.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 9 - Net Tax After TMT
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Subtract <span style={{ fontWeight: 600 }}> Line 8</span>{' '}
                    from <span style={{ fontWeight: 600 }}> Line 7</span> and
                    enter the result on Line 9. This amount is used to determine
                    the credit that may be claimed on Form 8911.
                  </li>
                  <li>
                    If the result is zero or less, enter{' '}
                    <span style={{ fontWeight: 600 }}>0</span> on Line 9.
                    Generally, you do not need to continue filing Form 8911
                    unless you are claiming a credit reported on{' '}
                    <span style={{ fontWeight: 600 }}> Line 3</span>.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 10 - Credit Allowed After Tax Liability Limit
                </Typography>

                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Line 10 is used to determine the portion of the credit that
                    can be claimed after applying the tax liability limitations.
                  </li>
                  Any unused portion of the{' '}
                  <span style={{ fontWeight: 600 }}>personal credit</span> that
                  cannot be claimed because of the tax liability limit is lost.
                  The unused credit cannot be carried back to a prior tax year
                  or carried forward to a future tax year.
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="scha"
                >
                  Schedule A (Form 8911) - Alternative Fuel Vehicle Refueling
                  Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Schedule A (Form 8911)
                  </span>{' '}
                  is used to calculate the{' '}
                  <span style={{ fontStyle: 'italic' }}>
                    {' '}
                    “Alternative Fuel Vehicle Refueling Property”
                  </span>{' '}
                  Credit for each qualified refueling property placed in service
                  during the tax year. The information reported on Schedule A is
                  used to determine the credit that flows to Form 8911.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  A{' '}
                  <span style={{ fontWeight: 600 }}> separate Schedule A </span>{' '}
                  must be completed{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    for each qualified alternative fuel vehicle refueling
                    property
                  </span>{' '}
                  for which the credit is claimed. Taxpayers with multiple
                  qualifying properties must attach a separate Schedule A for
                  each property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  <span style={{ fontWeight: 600 }}> Schedule A</span> also
                  includes information about the property's
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 4,
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li>Location,</li>
                  <li>Qualified installation costs </li>
                  <li>Eligibility requirements, and </li>
                  <li>The calculation of the allowable credit amount. </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="howto"
                >
                  How to Complete Schedule A (Form 8911): Line-by-Line
                  Instructions
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8911-scha-alternative-img.webp" />
                  <source srcSet="/assets/images/f8911-scha-alternative-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8911-scha-alternative-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8911) - Alternative Fuel Vehicle Refueling Property Organization details"
                    width={760}
                    height={147}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Name(s) Shown on Return
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the taxpayer's name exactly as it appears on the federal
                  tax return to which Schedule A (Form 8911) is attached.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Identifying Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Enter the taxpayer's identifying number.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Individuals should enter their Social Security Number (SSN).
                  </li>
                  <li>
                    Businesses and tax-exempt organizations should enter their
                    Employer Identification Number (EIN).{' '}
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <strong style={{ fontWeight: 600 }}>Note :</strong> A separate{' '}
                  <span style={{ fontWeight: 600 }}>
                    Schedule A (Form 8911)
                  </span>{' '}
                  must be completed and attached for each qualified alternative
                  fuel vehicle refueling property placed in service during the
                  tax year. If you are claiming the credit for multiple
                  qualifying properties, you must file a separate Schedule A for
                  each property.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 84,
                  }}
                  id="part1"
                >
                  Part I - Vehicle Refueling Property Details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8911-scha-part1-img.webp" />
                  <source srcSet="/assets/images/f8911-scha-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8911-scha-part1-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8911) Part I - Vehicle Refueling Property Details Line 1 - Line 7"
                    width={760}
                    height={320}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1 - Registration Number
                </Typography>
                <Typography variant="body1">
                  Enter the IRS-issued registration number if you are:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    An applicable entity making an{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Elective Payment Election (EPE)
                    </span>
                    , or
                  </li>
                  <li>
                    An eligible taxpayer, partnership, or S corporation electing
                    to transfer all or a portion of the credit.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The registration number is obtained by completing the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    IRS pre-filing registration proces
                  </span>{' '}
                  for each qualified refueling property for which the credit is
                  claimed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you are not making an Elective Payment Election (EPE) or a
                  transfer election, you are not required to enter a
                  registration number on Line 1.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2a - Description of Refueling Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Briefly describe the{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    nature of your qualified refueling property
                  </span>
                  . For instance, indicate whether the property installed in
                  service refers to{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    electric vehicle charging stations
                  </span>{' '}
                  or{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    other related refueling properties
                  </span>
                  .
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2b - Property Owner Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete this section if the owner of the refueling property
                  is different from the taxpayer claiming the credit.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b(i) – Owner's Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 2b(i) – Owner's Name Specify the actual{' '}
                  <span style={{ fontWeight: 600 }}>legal name</span> of the
                  property owner.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2b(ii) – Owner's TIN
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the{' '}
                  <span style={{ fontWeight: 600 }}>
                    Taxpayer Identification Number (TIN)
                  </span>{' '}
                  of the property owner.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 3 is used to report the location of the qualified
                  refueling property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 3 is used to report the location of the qualified
                  refueling property.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3a - Property Address
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the actual physical location or address of the
                  property, if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3b - Geographic Coordinates
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the geographic coordinates of the property's location.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3b(i) - Latitude
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the latitude coordinates. Include{' '}
                  <span style={{ fontWeight: 600 }}>plus (+) or minus (-)</span>{' '}
                  sign in the first box depending on the coordinates' value.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3b(ii) - Longitude
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  State the longitude coordinates. Include{' '}
                  <span style={{ fontWeight: 600 }}>plus (+) or minus (-)</span>{' '}
                  sign in the first box, as applicable.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 4 - Date Construction Began
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.8 }}>
                  Enter the date construction of the qualified refueling
                  property began. The construction start date must be determined
                  using one of the IRS-approved methods for establishing the
                  beginning of construction.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>The Physical Work Test</li>
                  <li>The Five Percent Safe Harbor method</li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 5 - Date Placed in Service
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the date the refueling property was placed in service.
                  </li>
                  <li>
                    Generally, property is considered placed in service when it
                    is ready and available for its intended use, regardless of
                    whether it is actively being used on that date.
                  </li>
                </Box>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <strong style={{ fontWeight: 600 }}>Example:</strong> An
                  electric vehicle charging station installed at a taxpayer's
                  residence is considered placed in service once it is fully
                  operational and available for use.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 6 - Eligible Census Tract Information
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Line 6 is used to determine whether the refueling property is
                  located in an eligible census tract.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6a - Eligible Census Tract
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check the appropriate box to indicate whether the property is
                  located in an eligible census tract.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To determine eligibility, use the property's address or
                  geographic coordinates to identify its census tract and verify
                  that the tract is included in the IRS list of eligible census
                  tracts.
                </Typography>

                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6b - Census Tract GEOID
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the property is located in an eligible census tract, enter
                  the applicable{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    11-digit census tract GEOID
                  </span>
                  .
                </Typography>
                <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mt: 0, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                  id="filing"
                >
                  <strong style={{ fontWeight: 600 }}>Note:</strong> If the
                  property's census tract is ineligible, the property does not
                  qualify for the Alternative Fuel Vehicle Refueling Property
                  Credit.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 7 - Certification or Permit Number
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the{' '}
                  <span style={{ fontWeight: 600 }}>
                    certification number, permit number,
                  </span>{' '}
                  or both, issued by a state or local government for the
                  refueling property, if applicable.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This information helps verify that the property complies with
                  applicable state or local safety requirements. If no
                  certification or permit number is required for the property,
                  leave Line 7 blank.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part II - Credit Amount for Business/Investment Use Part of
                  Refueling Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8911-scha-part2-img.webp" />
                  <source srcSet="/assets/images/f8911-scha-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8911-scha-part2-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8911) Part II - Credit Amount for Business/Investment Use Part of Refueling Property Line 8 - Line 16"
                    width={760}
                    height={211}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 8 - Cost of Qualified Refueling Property
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total cost of the qualified alternative fuel vehicle
                  refueling property described on Schedule A.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 9 - Business/Investment Use Percentage
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the percentage of business or investment use for the
                  refueling property.
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}>100%</span> if the
                    property is used entirely for business or investment
                    purposes.
                  </li>
                  <li>
                    If the property is used for both business and personal
                    purposes, enter only the percentage attributable to business
                    or investment use.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the property's use changes during the tax year from
                  personal use to business/investment use, or vice versa,
                  determine the business/investment use percentage based on the
                  portion of the year the property was used for business or
                  income-producing activities.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 10 - Business/Investment Use Cost
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Multiply <span style={{ fontWeight: 600 }}>Line 8</span> by{' '}
                    <span style={{ fontWeight: 600 }}>Line 9</span> and enter
                    the result on Line 10. This amount represents the portion of
                    the property's cost attributable to business or investment
                    use.
                  </li>
                  <li>
                    If the result is zero, enter{' '}
                    <span style={{ fontWeight: 600 }}>0</span> on Line 10, skip{' '}
                    <span style={{ fontWeight: 600 }}>Lines 11 through 16</span>
                    , and proceed directly to{' '}
                    <span style={{ fontWeight: 600 }}>Line 17</span>.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 11 - Section 179 Expense Deduction
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter any{' '}
                  <span style={{ fontWeight: 600 }}>
                    Section 179 expense deduction
                  </span>{' '}
                  claimed for the qualified refueling property on{' '}
                  <span style={{ fontWeight: 600 }}>Form 4562, Part I</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 12 - Adjusted Property Cost
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract <span style={{ fontWeight: 600 }}>Line 11</span> from{' '}
                  <span style={{ fontWeight: 600 }}>Line 10</span> and enter the
                  result on Line 12.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 13 - Prevailing Wage and Apprenticeship (PWA)
                  Requirements
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Indicate whether the qualified alternative fuel vehicle
                    refueling project meets the applicable{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Prevailing Wage and Apprenticeship (PWA) requirements
                    </span>{' '}
                    by checking <span style={{ fontWeight: 600 }}>
                      “Yes”
                    </span>{' '}
                    or <span style={{ fontWeight: 600 }}>“No.</span>”
                  </li>
                  <li>
                    Projects that satisfy the PWA requirements may qualify for
                    an increased credit rate.
                  </li>
                  <li>
                    If construction of the refueling property began{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      before January 29, 2023,
                    </span>{' '}
                    check <span style={{ fontWeight: 600 }}>Yes</span> on Line
                    13 without applying the PWA requirements.
                  </li>
                  <li>
                    Taxpayers claiming the increased credit amount based on
                    compliance with the PWA requirements must file a separate
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Form 7220, Prevailing Wage and Apprenticeship (PWA)
                      Verification and Corrections,
                    </span>{' '}
                    for each qualified refueling property.
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 14 - Credit Calculation
                </Typography>
                <Typography variant="body1">Multiply Line 12 by:</Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> 6% (0.06)</span> if the
                    property does not meet the PWA requirements, or{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> 30% (0.30)</span> if the
                    property meets the PWA requirements.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the result on Line 14.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 15 - Maximum Credit Limit
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The maximum credit allowed for the business or investment-use
                  portion of a qualified refueling property is{' '}
                  <span style={{ fontWeight: 600 }}>$100,000</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 16 - Allowed Business/Investment Use Credit
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the smaller value between Line 14 and{' '}
                    <span style={{ fontWeight: 600 }}>Line 15</span>. Include
                    this amount on{' '}
                    <span style={{ fontWeight: 600 }}>Line 1 of Form 8911</span>
                    .{' '}
                  </li>
                  <li>
                    If you enter <span style={{ fontWeight: 600 }}>100%</span>{' '}
                    on <span style={{ fontWeight: 600 }}>Line 9</span>, stop
                    here. Otherwise, continue to{' '}
                    <span style={{ fontWeight: 600 }}>Line 17</span>.{' '}
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Part III - Credit Amount for Personal Use Part of Refueling
                  Property
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f8911-scha-part3-img.webp" />
                  <source srcSet="/assets/images/f8911-scha-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f8911-scha-part3-img.png"
                    loading="lazy"
                    alt="Schedule A (Form 8911) Part III - Credit Amount for Personal Use Part of Refueling Property Line 17 - Line 21"
                    width={760}
                    height={147}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 2,
                    }}
                  />
                </picture>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 17 - Property Installed at Your Main Home
                </Typography>
                <Typography variant="body1">
                  Indicate whether the qualified refueling property was
                  installed on property used as your main home.{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 1, '& li': { mb: 1.2 } }}>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>“Yes”</span> if the
                    property was installed at your main residence and continue
                    to <span style={{ fontWeight: 600 }}>Line 18</span>.{' '}
                  </li>
                  <li>
                    Select <span style={{ fontWeight: 600 }}>“No”</span> if the
                    property was not installed at your main residence.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Refueling property that is not installed on property used as
                  your main home does not qualify for the personal-use portion
                  of the credit.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 19 - Personal-Use Credit Calculation
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Multiply <span style={{ fontWeight: 600 }}>Line 18</span> by{' '}
                  <span style={{ fontWeight: 600 }}>30% (0.30)</span> and enter
                  the result on Line 19.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 20 - Maximum Personal-Use Credit
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The maximum credit allowed for the personal-use portion of a
                  qualified refueling property is{' '}
                  <span style={{ fontWeight: 600 }}>$1,000</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Line 21 - Allowed Personal-Use Credit
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the smaller value from either{' '}
                    <span style={{ fontWeight: 600 }}> Line 19</span> or{' '}
                    <span style={{ fontWeight: 600 }}> Line 20</span>. This
                    amount is the allowable personal-use credit for the
                    property.
                  </li>
                  <li>
                    Include this amount on{' '}
                    <span style={{ fontWeight: 600 }}>
                      Line 4 in Part II of Form 8911
                    </span>{' '}
                  </li>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided in this resource is based on
                        official guidance published by the Internal Revenue
                        Service (IRS).
                        <ul>
                          <li>
                            {' '}
                            <Link
                              href="https://www.irs.gov/pub/irs-pdf/i8911.pdf"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Instructions
                            </Link>{' '}
                            for Form 891, Alternative Fuel Vehicle Refueling
                            Property Credit{' '}
                          </li>
                          <li>
                            <Link
                              href="https://www.irs.gov/instructions/i7220"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              {' '}
                              Form 7220
                            </Link>
                            , Prevailing Wage and Apprenticeship (PWA)
                            Verification and Corrections
                          </li>
                          <li>
                            <Link
                              href="https://www.irs.gov/instructions/i3800"
                              target="_blank"
                              sx={{ color: '#0202ff' }}
                            >
                              Instructions
                            </Link>{' '}
                            for Form 3800, General Business Credit
                          </li>
                        </ul>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" id="common" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
            className="responsive"
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
              }}
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What qualifies as alternative fuel vehicle refueling
                  property?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                      Alternative fuel vehicle refueling property includes
                      equipment for charging electric vehicles, hydrogen fuel
                      cell stations, and natural gas refueling installations
                      that meet IRS eligibility criteria.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is considered as a fuel cell property?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography variant="body1" sx={{ mb: 1, mt: 1 }}>
                      A Fuel cell property is an equipment that uses fuel cell
                      technology to{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        generate electricity through a chemical reaction
                      </span>{' '}
                      rather than by burning fuel. To qualify for a tax credit,
                      the property must meet applicable IRS requirements and
                      performance standards.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. How does Form 8911 apply to electric vehicles?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div" sx={{ mt: 1 }}>
                      Form 8911 provides a credit for qualified alternative fuel
                      vehicle refueling property, such as electric vehicle
                      charging stations. The credit applies to the charging
                      equipment and installation costs, not to the purchase or
                      lease of an electric vehicle.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  4. What is the maximum credit amount available on Form 8911?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div" sx={{ mt: 1 }}>
                      The maximum credit depends on how the qualified refueling
                      property is used:
                    </Typography>
                    <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                      <li>
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          Business or investment-use property:
                        </span>{' '}
                        Up to <span style={{ fontWeight: 600 }}>
                          $100,000
                        </span>{' '}
                        per qualified property.
                      </li>
                      <li>
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          Personal-use property installed at your main home:
                        </span>{' '}
                        Up to
                        <span style={{ fontWeight: 600 }}> $1,000</span> per
                        qualified property.
                      </li>
                    </Box>
                    <Typography component="div" sx={{ mt: 1 }}>
                      The actual credit amount is based on the property's
                      eligible costs and applicable IRS limitations.{' '}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  5. What types of vehicle conversions qualify for the Form 8911
                  credit?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography component="div" sx={{ mt: 1 }}>
                      Form 8911 does not provide credit for vehicle conversions.
                      Instead, it applies to{' '}
                      <span style={{ fontWeight: 600 }}>
                        qualified alternative fuel vehicle refueling property,
                      </span>{' '}
                      such as{' '}
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        EV charging stations
                      </span>{' '}
                      and{' '}
                      <span style={{ fontWeight: 600 }}>
                        other eligible refueling equipment
                      </span>
                      . The credit is based on the installation of the refueling
                      property, not modifications made to a vehicle.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form8911;
